import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import Tooltip from '@material-ui/core/Tooltip';
import SettingsIcon from '@material-ui/icons/Settings';
import ToggleInput from '@/se/components/inputs/ToogleInput';
import NumberInput from '@/se/components/inputs/NumberInput';
import LabelComponent, { withLabel } from '@/se/components/Label';
import ToggleGroup from '../../../shared/ToggleGroup';
import SchedulePdfsMultipleSelectInput from '../../SchedulePdfsMultipleSelectInput';
import AnalyticsPdfsMultipleSelectInput from '../../AnalyticsPdfsMultipleSelectInput';
import { HospitalFormValue, HospitalModules } from '../types';

const LabeledSchedulePDFs = withLabel('Select Schedule PDFs')(SchedulePdfsMultipleSelectInput) as any;
const LabeledAnalyticsPDFs = withLabel('Select Analytics PDFs')(AnalyticsPdfsMultipleSelectInput) as any;
const LabeledPriorityCounter = withLabel('Priority Counter')(NumberInput) as any;
const LabeledRetainedHorizon = withLabel('Retained beacon return horizon (minutes)')(NumberInput) as any;
const LabeledRetainedPreWindow = withLabel('Retained beacon pre-schedule window (minutes)')(NumberInput) as any;
const LabeledRetainedRetry = withLabel('Retained beacon OR retry interval (seconds)')(NumberInput) as any;
const Label = LabelComponent as any;

type RoomFeed = 'hl7' | 'csv';

interface RoomPolicyToggleProps {
  label: string;
  value: boolean;
  onChange: (val: boolean) => void;
  hl7Disabled: boolean;
  csvDisabled: boolean;
  onExceptionChange: (feed: RoomFeed, disabled: boolean) => void;
  disabled?: boolean;
  conflictDisabled?: boolean;
  disabledReason?: string;
}

// An org-level room policy toggle with a cog that opens per-feed "disable"
// exceptions. The cog is shown only while the policy is on (exceptions are gated).
const RoomPolicyToggle: React.FC<RoomPolicyToggleProps> = ({
  label,
  value,
  onChange,
  hl7Disabled,
  csvDisabled,
  onExceptionChange,
  disabled,
  conflictDisabled,
  disabledReason,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const showCog = value && !disabled;
  const switchInput = (
    <ToggleInput label="" value={value} onChange={onChange} isDisabled={disabled || conflictDisabled} />
  );

  // One Label row matching the other toggles. The switch is anchored right; the
  // cog sits to its left in the same centered cluster, so showing/hiding the cog
  // never moves the switch.
  return (
    <Label text={label} style={{ flexFlow: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box display="flex" alignItems="center" style={{ gap: 4 }}>
        {showCog && (
          <IconButton
            size="small"
            style={{ padding: 4 }}
            aria-label={`${label} per-feed exceptions`}
            onClick={e => setAnchorEl(e.currentTarget)}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        )}
        {conflictDisabled && disabledReason ? (
          <Tooltip title={disabledReason}>
            <span style={{ display: 'inline-flex' }}>{switchInput}</span>
          </Tooltip>
        ) : (
          switchInput
        )}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableScrollLock
        >
          <Box p={2} minWidth={240} display="flex" flexDirection="column" style={{ gap: 8 }}>
            <ToggleInput
              label="Disable for HL7"
              value={hl7Disabled}
              onChange={(v: boolean) => onExceptionChange('hl7', v)}
              isDisabled={disabled}
            />
            <ToggleInput
              label="Disable for CSV"
              value={csvDisabled}
              onChange={(v: boolean) => onExceptionChange('csv', v)}
              isDisabled={disabled}
            />
          </Box>
        </Popover>
      </Box>
    </Label>
  );
};

interface ModulesTabProps {
  value: HospitalFormValue;
  onChange: (value: HospitalFormValue) => void;
  disabled?: boolean;
}

const ModulesTab: React.FC<ModulesTabProps> = ({ value, onChange, disabled }) => {
  const modules = value.modules || ({} as HospitalModules);
  const retainedBeaconInpatient = modules.retainedBeaconInpatient || {
    enabled: false,
    maxReturnHorizonMinutes: 2880,
    preScheduleActivationMinutes: 120,
    pendingRetrySeconds: 30,
  };

  const handleModule = (field: keyof HospitalModules) => (val: any) => {
    onChange({
      ...value,
      modules: {
        ...modules,
        [field]: val,
      },
    });
  };

  const handlePreOpPriority = (field: string) => (val: any) => {
    onChange({
      ...value,
      modules: {
        ...modules,
        preOpPriority: {
          ...(modules.preOpPriority || { hasPreOpPriority: false, priorityCounter: 0 }),
          [field]: val,
        },
      },
    });
  };

  const handleRetainedBeaconInpatient = (field: keyof typeof retainedBeaconInpatient) => (val: boolean | number) => {
    onChange({
      ...value,
      modules: {
        ...modules,
        retainedBeaconInpatient: {
          ...retainedBeaconInpatient,
          [field]: val,
        },
      },
    });
  };

  return (
    <Box>
      <ToggleGroup title="Patient Flow">
        <ToggleInput
          label="Patient Enrollment"
          value={modules.patientRegistration}
          onChange={handleModule('patientRegistration')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Automatic Patient Tracking"
          value={modules.automaticPatientTracking}
          onChange={handleModule('automaticPatientTracking')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Has Patient Types"
          value={modules.hasPatientTypes}
          onChange={handleModule('hasPatientTypes')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Has Post OP"
          value={modules.hasPostop}
          onChange={handleModule('hasPostop')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Accepts Procedure Transfers"
          value={modules.acceptsProcedureTransfer}
          onChange={handleModule('acceptsProcedureTransfer')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Auto Discharge Before 24h In"
          value={modules.autoDischarge}
          onChange={handleModule('autoDischarge')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Auto Physician In"
          value={modules.autoPhysicianIn}
          onChange={handleModule('autoPhysicianIn')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Auto Procedure Room Change"
          value={modules.autoProcedureRoomChange}
          onChange={handleModule('autoProcedureRoomChange')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Retained Beacon for InPatients"
          value={retainedBeaconInpatient.enabled}
          onChange={handleRetainedBeaconInpatient('enabled')}
          isDisabled={disabled}
        />
        {retainedBeaconInpatient.enabled && (
          <Box gridColumn="1 / -1" display="flex" flexDirection="column" width="calc(50% - 16px)" minWidth={0}>
            <LabeledRetainedHorizon
              min={1}
              max={10080}
              value={retainedBeaconInpatient.maxReturnHorizonMinutes}
              onChange={handleRetainedBeaconInpatient('maxReturnHorizonMinutes')}
              disabled={disabled}
            />
            <LabeledRetainedPreWindow
              min={0}
              max={1440}
              value={retainedBeaconInpatient.preScheduleActivationMinutes}
              onChange={handleRetainedBeaconInpatient('preScheduleActivationMinutes')}
              disabled={disabled}
            />
            <LabeledRetainedRetry
              min={5}
              max={300}
              value={retainedBeaconInpatient.pendingRetrySeconds}
              onChange={handleRetainedBeaconInpatient('pendingRetrySeconds')}
              disabled={disabled}
            />
          </Box>
        )}
        <ToggleInput
          label="Pre-Op Status Reset"
          value={modules.preOpStatusReset}
          onChange={handleModule('preOpStatusReset')}
          isDisabled={disabled}
        />
      </ToggleGroup>

      <ToggleGroup title="Pre-Op Priority">
        <ToggleInput
          label="Has PreOp Priority"
          value={modules.preOpPriority?.hasPreOpPriority}
          onChange={handlePreOpPriority('hasPreOpPriority')}
          isDisabled={disabled}
        />
        {modules.preOpPriority?.hasPreOpPriority && (
          <LabeledPriorityCounter
            value={modules.preOpPriority?.priorityCounter || 0}
            onChange={handlePreOpPriority('priorityCounter')}
            disabled={disabled}
            min={0}
          />
        )}
      </ToggleGroup>

      <ToggleGroup title="Schedule">
        <ToggleInput
          label="Block Schedule"
          value={modules.blockSchedule}
          onChange={handleModule('blockSchedule')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Legacy Block Schedule"
          value={modules.legacyBlockSchedule}
          onChange={handleModule('legacyBlockSchedule')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Create Procedure On Schedule"
          value={modules.createProcedureSchedule}
          onChange={handleModule('createProcedureSchedule')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Upload Schedule File"
          value={modules.showCSVUpload}
          onChange={handleModule('showCSVUpload')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Show Weekends on Weekly/Monthly Schedule"
          value={modules.showWeekends}
          onChange={handleModule('showWeekends')}
          isDisabled={disabled}
        />
        <Box gridColumn="1 / -1">
          <LabeledSchedulePDFs
            value={modules.schedulePDFs}
            onChange={handleModule('schedulePDFs')}
            disabled={disabled}
          />
        </Box>
      </ToggleGroup>

      <ToggleGroup title="Tablet & Charting">
        <ToggleInput
          label="Pre-Op and PACU Note on Tablet"
          value={modules.noteTablet}
          onChange={handleModule('noteTablet')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Procedure Pre-Op and PACU Note on Tablet"
          value={modules.procedureNoteTablet}
          onChange={handleModule('procedureNoteTablet')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Patient Chart"
          value={modules.patientCharting}
          onChange={handleModule('patientCharting')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Use OR Tablet v2"
          value={modules.orTabletV2}
          onChange={handleModule('orTabletV2')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Enable Registration Package"
          value={modules.inTakeForms}
          onChange={handleModule('inTakeForms')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Registration Package Defaults to Today"
          value={modules.registrationDefaultToday}
          onChange={handleModule('registrationDefaultToday')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Enable Dictation"
          value={modules.dictation}
          onChange={handleModule('dictation')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Show QR Code on Forms"
          value={modules.showQRCode}
          onChange={handleModule('showQRCode')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Show PreOp Note on Forms"
          value={modules.showPreOpNote}
          onChange={handleModule('showPreOpNote')}
          isDisabled={disabled}
        />
      </ToggleGroup>

      <ToggleGroup title="Analytics">
        <ToggleInput
          label="Analytics V2"
          value={modules.analyticsV2}
          onChange={handleModule('analyticsV2')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Analytics V2 Pie Charts"
          value={modules.analyticsV2PieCharts}
          onChange={handleModule('analyticsV2PieCharts')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="CPT Analytics"
          value={modules.cptAnalytics}
          onChange={handleModule('cptAnalytics')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="PACU Analytics"
          value={modules.pacuAnalytics}
          onChange={handleModule('pacuAnalytics')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Anesthesiologist Utilization Analytics"
          value={modules.anesthesiologistUtilizationAnalytics}
          onChange={handleModule('anesthesiologistUtilizationAnalytics')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Staff Cost"
          value={modules.showStaffCost}
          onChange={handleModule('showStaffCost')}
          isDisabled={disabled}
        />
        <Box gridColumn="1 / -1">
          <LabeledAnalyticsPDFs
            value={modules.analyticsPDFs}
            onChange={handleModule('analyticsPDFs')}
            disabled={disabled}
          />
        </Box>
      </ToggleGroup>

      <ToggleGroup title="Sensors & Tracking">
        <ToggleInput
          label="Temperature Sensors and Reporting"
          value={modules.temperatureReporting}
          onChange={handleModule('temperatureReporting')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Air Filtration Warning"
          value={modules.airFiltrationTimer}
          onChange={handleModule('airFiltrationTimer')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Light Mode"
          value={modules.lightMode}
          onChange={handleModule('lightMode')}
          isDisabled={disabled}
        />
        <ToggleInput
          label="Staff Tracking"
          value={modules.showStaffTracking}
          onChange={handleModule('showStaffTracking')}
          isDisabled={disabled}
        />
      </ToggleGroup>

      <ToggleGroup title="Integration">
        <RoomPolicyToggle
          label="Schedule never updates room"
          value={!!modules.freezeRoomUpdates}
          onChange={handleModule('freezeRoomUpdates')}
          hl7Disabled={!!modules.hl7FreezeRoomDisabled}
          csvDisabled={!!modules.csvFreezeRoomDisabled}
          onExceptionChange={(feed, d) =>
            handleModule(feed === 'hl7' ? 'hl7FreezeRoomDisabled' : 'csvFreezeRoomDisabled')(d)
          }
          disabled={disabled}
          conflictDisabled={!!modules.respectManualRoomOverride}
          disabledReason={
            'Disabled because "Manual room changes override schedule updates" is enabled; the two room policies are mutually exclusive.'
          }
        />
        <RoomPolicyToggle
          label="Manual room changes override schedule updates"
          value={!!modules.respectManualRoomOverride}
          onChange={handleModule('respectManualRoomOverride')}
          hl7Disabled={!!modules.hl7ManualOverrideDisabled}
          csvDisabled={!!modules.csvManualOverrideDisabled}
          onExceptionChange={(feed, d) =>
            handleModule(feed === 'hl7' ? 'hl7ManualOverrideDisabled' : 'csvManualOverrideDisabled')(d)
          }
          disabled={disabled}
          conflictDisabled={!!modules.freezeRoomUpdates}
          disabledReason={
            'Disabled because "Schedule never updates room" is enabled; the two room policies are mutually exclusive.'
          }
        />
        <ToggleInput
          label="Enable Secondary Room Anesthesiologist"
          value={modules.secondaryRoomAnesthesiologist}
          onChange={handleModule('secondaryRoomAnesthesiologist')}
          isDisabled={disabled}
        />
      </ToggleGroup>

      <ToggleGroup title="IP Whitelist" columns={1}>
        <ToggleInput
          label="Charting IP Address Whitelist"
          value={modules.chartingIPAddressWhitelistEnabled}
          onChange={handleModule('chartingIPAddressWhitelistEnabled')}
          isDisabled={disabled}
        />
        {modules.chartingIPAddressWhitelistEnabled && (
          <input
            type="text"
            value={modules.chartingIPAddressWhitelist || ''}
            onChange={e => handleModule('chartingIPAddressWhitelist')(e.target.value)}
            placeholder="Comma-separated IP addresses"
            disabled={disabled}
            style={{
              padding: '0.5rem',
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'transparent',
              color: 'inherit',
              marginBottom: '0.5rem',
            }}
          />
        )}
        <ToggleInput
          label="IP Address Whitelist"
          value={modules.ipAddressWhitelistEnabled}
          onChange={handleModule('ipAddressWhitelistEnabled')}
          isDisabled={disabled}
        />
        {modules.ipAddressWhitelistEnabled && (
          <input
            type="text"
            value={modules.ipAddressWhitelist || ''}
            onChange={e => handleModule('ipAddressWhitelist')(e.target.value)}
            placeholder="Comma-separated IP addresses"
            disabled={disabled}
            style={{
              padding: '0.5rem',
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'transparent',
              color: 'inherit',
            }}
          />
        )}
      </ToggleGroup>
    </Box>
  );
};

export default ModulesTab;
